"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useT } from "@/hooks/useT";

interface PlatformStats {
  total_users: number;
  total_volume: number;
  total_trades: number;
  total_markets: number;
}

// Stat schema; the per-locale label/description are resolved inside the
// component via t() so the array can stay module-scoped.
const STAT_KEYS = [
  { key: "total_volume", suffix: "+", prefix: "$", labelKey: "stats.totalVolume.label", descKey: "stats.totalVolume.desc" },
  { key: "total_users", suffix: "+", prefix: "", labelKey: "stats.activeTraders.label", descKey: "stats.activeTraders.desc" },
  { key: "total_markets", suffix: "+", prefix: "", labelKey: "stats.activeMarkets.label", descKey: "stats.activeMarkets.desc" },
  { key: "total_trades", suffix: "+", prefix: "", labelKey: "stats.tradesExecuted.label", descKey: "stats.tradesExecuted.desc" },
] as const;

function formatNumber(num: number, prefix: string, suffix: string): string {
  const rounded = Math.round(num);
  if (rounded >= 1000000) {
    return `${prefix}${(rounded / 1000000).toFixed(1)}M${suffix}`;
  }
  if (rounded >= 1000) {
    return `${prefix}${(rounded / 1000).toFixed(rounded >= 100000 ? 0 : 1)}K${suffix}`;
  }
  return `${prefix}${rounded}${suffix}`;
}

function Counter({ value, prefix, suffix }: { value: number; prefix: string; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    const stepDuration = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {formatNumber(count, prefix, suffix)}
    </span>
  );
}

export default function StatsSection() {
  const t = useT();
  const [values, setValues] = useState<Record<string, number>>({});

  useEffect(() => {
    async function fetchStats() {
      try {
        const response = await fetch('/api/stats/platform');
        const data = await response.json();
        if (data.success && data.stats) {
          setValues(data.stats);
        }
      } catch (error) {
      }
    }
    fetchStats();
  }, []);

  const stats = STAT_KEYS.map(s => ({
    ...s,
    value: Math.round(values[s.key] || 0),
    label: t(s.labelKey),
    description: t(s.descKey),
  }));

  return (
    <section className="relative py-8 sm:py-24 overflow-hidden bg-gray-50 dark:bg-[#0f0f12]">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
            {t("stats.headline.before")}{" "}
            <span className="text-accent-primary">
              {t("stats.headline.accent")}
            </span>
          </h2>
          <p className="text-sm sm:text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t("stats.subtitle")}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="relative h-full bg-white dark:bg-[#18181D] rounded-[5px] sm:rounded-[5px] p-3 sm:p-8 border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
                <div className="relative z-10">
                  <div className="text-2xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 tracking-tight">
                    <Counter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5 sm:mb-1">
                    {stat.label}
                  </div>
                  <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                    {stat.description}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-4 sm:mt-12"
        >
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {t("stats.footnote")}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
